import { Star, Quote, ShieldCheck, Phone, ArrowRight, MessageSquareQuote, BadgeCheck, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { stockImages } from "@/data/site/stockImages";
import { PageSEO } from "@/components/site/PageSEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { reviews, reviewAggregate } from "@/data/site/reviews";
import { brand } from "@/data/site/brand";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";

const platformRatings = [
  { name: "Google", rating: 5.0, count: 32 },
  { name: "Better Business Bureau", rating: 5.0, count: 8, note: "A+ Accredited" },
  { name: "Yelp", rating: 5.0, count: 6 },
  { name: "AICPA-credentialed CPA firm", rating: null, count: null, note: "Active license · Good standing" },
];

const reviewThemes = [
  { icon: BadgeCheck, label: "CPA-led from day one", detail: "Clients are not handed to junior staff or a sales closer." },
  { icon: MessageSquareQuote, label: "Honest read on the case", detail: "Straight answer — even when the answer is not what someone hoped to hear." },
  { icon: ShieldCheck, label: "Files what was promised", detail: "Powers of Attorney, transcripts, OICs, and IAs filed on time, every time." },
];

const Reviews = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: brand.legalName,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: reviewAggregate.ratingValue,
      reviewCount: reviewAggregate.reviewCount,
    },
    review: reviews.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      reviewRating: { "@type": "Rating", ratingValue: r.rating },
      datePublished: r.date,
      reviewBody: r.body,
    })),
  };

  return (
    <SiteLayout>
      <PageSEO
        title="Client Reviews"
        description="Real client reviews of Comprehensive Tax Resolution LLC — IRS tax resolution outcomes from across the country."
        path="/reviews"
        image={stockImages.consultation.src}
        jsonLd={jsonLd}
      />

      <section className="relative bg-gradient-navy text-white overflow-hidden">
        <HeroBackground image={stockImages.consultation} priority />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
          <Reveal>
            <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Reviews" }]} tone="dark" />
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold mt-2 mb-4">
              What clients say
            </p>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance]">
              Reviews from real<br />
              <span className="text-gold">tax-resolution clients.</span>
            </h1>
            <p className="text-lg text-white/80 max-w-2xl leading-relaxed mb-8">
              Unedited stories from taxpayers and business owners who came to us with active
              collections, audits, payroll-tax problems, and six-figure balances. Verified across
              Google, BBB, and Yelp.
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-3">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-gold text-gold" />
                  ))}
                </div>
                <span className="text-lg font-semibold">
                  {reviewAggregate.ratingValue.toFixed(1)} · {reviewAggregate.reviewCount}+ reviews
                </span>
              </div>
              <span className="text-white/40">|</span>
              <span className="text-white/85 text-sm inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-gold" /> BBB A+ accredited · 15+ years
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Platform rating strip */}
      <section className="py-10 border-b border-border bg-secondary/40">
        <div className="container mx-auto px-4 max-w-5xl">
          <RevealStagger className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {platformRatings.map((p) => (
              <RevealItem key={p.name} className="text-center sm:text-left">
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">
                  {p.name}
                </p>
                {p.rating !== null ? (
                  <div className="flex items-center justify-center sm:justify-start gap-1.5 mb-1">
                    <span className="font-heading text-2xl font-bold text-navy">{p.rating.toFixed(1)}</span>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-gold text-gold" />
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="font-heading text-lg font-bold text-navy leading-tight">{p.note}</p>
                )}
                {p.rating !== null && (
                  <p className="text-xs text-muted-foreground">
                    {p.count} reviews{p.note ? ` · ${p.note}` : ""}
                  </p>
                )}
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Themes / what clients consistently say */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold mb-3">
              The themes that come up the most
            </p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-8 leading-tight max-w-3xl">
              Three things clients keep mentioning — across very different IRS cases.
            </h2>
          </Reveal>
          <RevealStagger className="grid md:grid-cols-3 gap-6">
            {reviewThemes.map((t) => (
              <RevealItem key={t.label}>
                <div className="rounded-lg border border-border bg-white p-6 h-full">
                  <div className="w-11 h-11 rounded bg-secondary inline-flex items-center justify-center mb-4">
                    <t.icon className="w-5 h-5 text-gold" />
                  </div>
                  <p className="font-heading font-bold text-navy mb-2">{t.label}</p>
                  <p className="text-sm text-foreground/75 leading-relaxed">{t.detail}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Reviews — magazine style */}
      <section className="py-16 lg:py-24 bg-secondary/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold mb-2">
                  Verified client reviews
                </p>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy leading-tight">
                  In their own words.
                </h2>
              </div>
              <p className="text-xs text-muted-foreground max-w-xs">
                Names shown as initial only when client preferred. Service tag links the review to
                the work category.
              </p>
            </div>
          </Reveal>

          <RevealStagger className="grid md:grid-cols-2 gap-6" stagger={60}>
            {reviews.map((r) => (
              <RevealItem key={r.id}>
                <article className="relative bg-white rounded-lg border border-border p-7 lg:p-8 h-full flex flex-col">
                  <Quote className="absolute top-5 right-5 w-8 h-8 text-gold/15" />
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex">
                      {[...Array(r.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-gold text-gold" />
                      ))}
                    </div>
                    <span className="text-[11px] text-muted-foreground">
                      {new Date(r.date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                    </span>
                  </div>
                  <blockquote className="text-foreground/85 leading-[1.75] text-[15px] mb-6 flex-1">
                    "{r.body}"
                  </blockquote>
                  <div className="pt-5 border-t border-border flex items-center justify-between text-sm">
                    <div>
                      <div className="font-semibold text-navy">{r.name}</div>
                      <div className="text-muted-foreground text-xs mt-0.5">{r.location}</div>
                    </div>
                    {r.service && (
                      <span className="text-[10px] font-bold text-gold uppercase tracking-[0.15em] text-right max-w-[45%]">
                        {r.service}
                      </span>
                    )}
                  </div>
                </article>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Want to see the numbers? */}
      <section className="py-14 bg-white border-t border-border">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold mb-3">
                Prefer the numbers?
              </p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-4 leading-tight">
                See the dollars behind the stories.
              </h2>
              <p className="text-foreground/80 leading-relaxed mb-6">
                Reviews tell you how it felt. Our case-results page shows the actual balances owed,
                what the IRS accepted, and how long it took — anonymized to protect clients but
                accurate on every number.
              </p>
              <Link
                to="/results"
                className="inline-flex items-center gap-2 text-navy font-semibold hover:text-gold"
              >
                View case results <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
            <Reveal>
              <div className="rounded-lg bg-gradient-navy text-white p-8 lg:p-10">
                <Building2 className="w-7 h-7 text-gold mb-4" />
                <p className="font-heading text-xl lg:text-2xl font-bold leading-tight mb-3">
                  Talk to the CPA who would actually handle your case.
                </p>
                <p className="text-white/80 text-sm leading-relaxed mb-6">
                  No call center. No bait-and-switch sales rep. The first voice you hear is the
                  professional running your file.
                </p>
                <a
                  href={`tel:${brand.phoneTel}`}
                  className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-navy font-bold px-6 py-3.5 rounded transition-transform hover:scale-[1.03]"
                >
                  <Phone className="w-5 h-5" /> Call {brand.phone}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default Reviews;
